import React, { useRef } from "react";
import { normalizeParam, denormalizeParam, clamp } from "../../lib/synthLab";
import { cn } from "../../lib/utils";

// The practice player's answer control: ONE parameter as a fat, thumb-sized
// horizontal fader you can grab anywhere on. Built pointer-first so it works
// the same with a finger on a phone, a mouse, or (via the parent wiring CC
// into `onChange`) a physical MIDI knob. Arrow keys nudge it for accessibility.
const BigFader = ({ param, value, onChange, disabled }) => {
  const trackRef = useRef(null);
  const n = normalizeParam(param.id, value);

  const setFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const t = clamp((clientX - rect.left) / rect.width, 0, 1);
    onChange(denormalizeParam(param.id, t));
  };

  const nudge = (delta) => onChange(denormalizeParam(param.id, clamp(n + delta, 0, 1)));

  return (
    <div className={cn(disabled && "pointer-events-none opacity-60")}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">{param.label}</span>
        <span className="font-mono text-sm text-muted-foreground">{param.format(value)}</span>
      </div>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={param.label}
        aria-valuemin={param.min}
        aria-valuemax={param.max}
        aria-valuenow={value}
        aria-valuetext={param.format(value)}
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          setFromClientX(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons & 1) setFromClientX(e.clientX);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            nudge(0.03);
          } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            nudge(-0.03);
          }
        }}
        className="relative mt-2 h-14 w-full cursor-pointer touch-none select-none overflow-hidden rounded-xl border border-border bg-secondary outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {/* fill */}
        <div
          className="absolute inset-y-0 left-0 bg-primary/25 transition-[width] duration-75"
          style={{ width: `${n * 100}%` }}
        />
        {/* thumb */}
        <div
          className="absolute top-1/2 h-10 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-primary shadow-md"
          style={{ left: `calc(${n * 100}% )` }}
        />
      </div>
    </div>
  );
};

export default BigFader;
